import { Node } from 'reactflow';
import { ValidationResult } from '../types/pipeline';

/**
 * Apply validation results to nodes by adding validation_errors and validation_warnings to node data
 * @param nodes - Current nodes in the pipeline
 * @param validationResults - Validation results from API
 * @returns Updated nodes with validation_errors and validation_warnings applied
 */
export function applyValidationToNodes(
  nodes: Node[],
  validationResults: ValidationResult | null
): Node[] {
  if (!validationResults) {
    // Clear all validation errors and warnings if no results
    return nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        validation_errors: undefined,
        validation_warnings: undefined,
      },
    }));
  }

  // Build maps of step_id -> messages
  const errorsByStepId = new Map<string, string[]>();
  const warningsByStepId = new Map<string, string[]>();
  
  validationResults.errors.forEach((error) => {
    if (error.step_id) {
      const existing = errorsByStepId.get(error.step_id) || [];
      errorsByStepId.set(error.step_id, [...existing, error.message]);
    }
  });

  validationResults.warnings.forEach((warning) => {
    if (warning.step_id) {
      const existing = warningsByStepId.get(warning.step_id) || [];
      warningsByStepId.set(warning.step_id, [...existing, warning.message]);
    }
  });

  // Apply errors and warnings to nodes
  return nodes.map((node) => {
    const nodeStepId = node.data.step_id || node.id;
    let errors = errorsByStepId.get(nodeStepId);
    let warnings = warningsByStepId.get(nodeStepId);

    // Check if this node is a sub-agent processor (node ID appears in compound step_ids)
    // Sub-agent step_ids look like: "orchestrator_123.subagent_name.processor_456.#"
    // We need to match node.id against the processor part
    if (!errors && !warnings) {
      for (const [stepId, stepErrors] of Array.from(errorsByStepId.entries())) {
        if (stepId.includes(node.id)) {
          errors = stepErrors;
          break;
        }
      }
    }
    if (!errors && !warnings) {
      for (const [stepId, stepWarnings] of Array.from(warningsByStepId.entries())) {
        if (stepId.includes(node.id)) {
          warnings = stepWarnings;
          break;
        }
      }
    }

    let updatedNode = {
      ...node,
      data: {
        ...node.data,
        validation_errors: errors && errors.length > 0 ? errors : undefined,
        validation_warnings: warnings && warnings.length > 0 ? warnings : undefined,
      },
    };

    // Apply validation to sub-agent nodes within orchestrators
    if (node.type === 'orchestrator' && node.data.config?.sub_agents) {
      const updatedSubAgents = node.data.config.sub_agents.map((subAgent: any) => {
        if (!subAgent.steps) return subAgent;

        const updatedSteps = subAgent.steps.map((step: any) => {
          const stepId = step.step_id;
          const stepErrors = errorsByStepId.get(stepId);
          const stepWarnings = warningsByStepId.get(stepId);

          return {
            ...step,
            validation_errors: stepErrors && stepErrors.length > 0 ? stepErrors : undefined,
            validation_warnings: stepWarnings && stepWarnings.length > 0 ? stepWarnings : undefined,
          };
        });

        return {
          ...subAgent,
          steps: updatedSteps,
        };
      });

      updatedNode = {
        ...updatedNode,
        data: {
          ...updatedNode.data,
          config: {
            ...updatedNode.data.config,
            sub_agents: updatedSubAgents,
          },
        },
      };
    }

    return updatedNode;
  });
}

/**
 * Clear validation errors from all nodes
 * @param nodes - Current nodes in the pipeline
 * @returns Nodes with validation_errors removed
 */
export function clearValidationFromNodes(nodes: Node[]): Node[] {
  return applyValidationToNodes(nodes, null);
}

